<template>
  <div
    ref="partialDiv"
    :asset-name="slug"
  >
    <section class="signup-success">
      <div
        :class="{
          'container mx-auto my-6': true,
        }"
      >
        <h2
          :class="{
            'signup-title': true,
          }"
        >
          {{ signupTitle }}
        </h2>

        <div
          :class="{
            'signup-msg success-msg mt-32px': true,
            'flex justify-center': true,
          }"
        >
          <div
            :class="{
              'w-[462px] rounded-md border border-[#dcdfeb] transition-all duration-300 ease-in-out hover:border-www-color-primary-lighter hover:shadow-card': true,
            }"
          >
            <div
              :class="{
                'card-body p-[25px]': true,
              }"
            >
              <ImageAtom
                v-if="image"
                v-bind="image"
                loading="eager"
              />

              <h3
                :class="{
                  'mt-6 text-center text-lg font-bold': true,
                }"
              >
                {{ cardTitle }}
              </h3>

              <p
                :class="{
                  email: true,
                  'text-center': true,
                }"
              >
                {{ emailValue }}
              </p>
            </div>
            <div
              :class="{
                'card-footer flex flex-col items-center border-t border-[#dcdfeb] bg-[#dcdfeb] bg-opacity-20 p-[15px] text-www-color-neutral-medium-dark': true,
              }"
            >
              <h6
                :class="{
                  'text-15px font-medium': true,
                }"
              >
                {{ cardSubtitle }}
              </h6>

              <ul
                :class="{
                  'list-disc px-6': true,
                }"
              >
                <template
                  v-for="(item, i) in signupMessageList"
                  :key="i"
                >
                  <li
                    :class="{
                      'link w-fit text-13px': true,
                    }"
                  >
                    <CodeEmbed v-bind="item" />
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
    <template v-if="screenPartialData?.sectionBuilder?.length > 0">
      <component
        :is="getComponent(component._type)"
        v-for="component in screenPartialData?.sectionBuilder"
        :key="component._key"
        :value="component"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { FieldHtmlNameUtil } from '@onshape/www-inertia';
import ImageAtom from '~/components/Atmos/ImageAtom/ImageAtom.vue';
import CodeEmbed from '~/components/Atmos/CodeEmbed/CodeEmbed.vue';
import FluentWaitUtil from '~/utils/FluentWaitUtil';
import UnifiedSignupPersonaDetails from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupPersonaDetails';
import UnifiedSignupPersona from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupPersona';
import UnifiedSignupPlan from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupPlan';
import UnifiedSignupIntent from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupIntent';
import type UnifiedSignupSessionInfo from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/session-info/UnifiedSignupSessionInfo';
import type SignupSuccessSignupScreen from '~/hekate-www/external/facets/Form/coordinator/UnifiedSignup/screens/SignupSuccessSignupScreen';
import type { SignupPartialViewsSectionBuilder, UssMessage } from '~/sanity/types';
import { getComponent } from '~/components/Mapper';

const props = defineProps<
  UssMessage & {
    signupScreen: SignupSuccessSignupScreen;
    sessionInfo: UnifiedSignupSessionInfo;
    signupSurveyDiv?: HTMLDivElement | null;
    screenPartialData: SignupPartialViewsSectionBuilder;
  }
>();

// Get email value from form field
const emailField = props.signupScreen.getField(FieldHtmlNameUtil.email);
const emailValue = computed(() => emailField.val as string);

// Template ref for DOM manipulation
const partialDiv = ref<HTMLDivElement | null>(null);
// Initialize survey listeners (moved from AbstractSignupScreenWithNewsletterForm.initListeners)
const initSurveyVisibility = async (): Promise<void> => {
  if (!partialDiv.value) {
    return;
  }

  // Wait up to 5 seconds for the resolutionActions to be set

  let replacement: Node = document.createTextNode('');
  let procedure = '';
  const resolutions = await FluentWaitUtil.whileNotFalsy({
    maxTries: 50,
    timeBetweenChecksInMilliseconds: 100,
    provider: () => props.sessionInfo.resolutionActions,
  }).catch(() => {
    console.warn(
      `Timed out waiting for resolutionActions on screen: ${props.signupScreen.screenName}`
    );
    return null;
  });
  // If we are not on the success screen and we haven't resolved the resolutions, we will not show the survey
  if (resolutions === null) {
    console.warn(
      `No resolutionActions found, not showing survey on non-success screen: ${props.signupScreen.screenName}`
    );
    return;
  }

  procedure = resolutions[0]?.procedure || '';
  replacement = document.createTextNode(resolutions[0]?.params?.replacement || '');

  const { personaDetails: chosenPersonaDetails } = props.sessionInfo;
  const { chosenPlan } = props.sessionInfo;
  const { intent: chosenIntent } = props.sessionInfo;
  const { persona: chosenPersona } = props.sessionInfo;
  if (!replacement || !chosenPlan || !chosenIntent || !chosenPersona) {
    console.warn(
      'Missing required information, not showing survey on non-success screen: uss-signup-email-status'
    );
    return;
  }

  // If persona is startup, show startup survey
  if (chosenPersonaDetails?.compareTo(UnifiedSignupPersonaDetails.startingNewBusiness)) {
    const signupSurveyStartupProgramElement = partialDiv.value.querySelector(
      '#signup-survey-startup-program'
    );
    if (signupSurveyStartupProgramElement) {
      signupSurveyStartupProgramElement.classList.remove('d-none');
    }
    // If persona is pro or biz intent show link to upcoming events
  } else if (
    UnifiedSignupPlan.pro.compareTo(chosenPlan) ||
    UnifiedSignupIntent.bizIntent.compareTo(chosenIntent) ||
    UnifiedSignupPersona.proPerson.compareTo(chosenPersona)
  ) {
    const signupSurveyUpcomingSessionsElement = partialDiv.value.querySelector(
      '#signup-survey-upcoming-sessions'
    );
    if (signupSurveyUpcomingSessionsElement) {
      signupSurveyUpcomingSessionsElement.classList.remove('d-none');
    }
    // If not pro and procedure is tellMeMoreAboutIt, show tell us more
  } else if (procedure === 'tellMeMoreAboutIt' && !UnifiedSignupPlan.pro.compareTo(chosenPlan)) {
    if (!props.signupSurveyDiv) {
      console.error('Signup survey div not set');
      return;
    }

    const signupSurveyTellUsMoreElement = partialDiv.value.querySelector(
      '#signup-survey-tell-us-more'
    );
    if (signupSurveyTellUsMoreElement) {
      signupSurveyTellUsMoreElement.classList.remove('d-none');
    }
    partialDiv.value.querySelector('#hekate-tell-more-about-replacement')?.appendChild(replacement);
    partialDiv.value
      .querySelector('#signup-survey-tell-us-more')
      ?.appendChild(props.signupSurveyDiv);

    const howDidYouHearAboutUsElement = partialDiv.value.querySelector(
      '[field-name="howDidYouHearAboutUs"]'
    );
    if (howDidYouHearAboutUsElement) {
      const labelElement = howDidYouHearAboutUsElement.querySelector('label') as HTMLLabelElement;
      labelElement.innerHTML = '';
    }

    const signupSurveyElement = partialDiv.value.querySelector('#signup-survey');
    if (signupSurveyElement) {
      signupSurveyElement.classList.remove('d-none');
    }

    const signupSurveySurveyFormElement = partialDiv.value.querySelector(
      '#signup-survey-surveyForm'
    );
    if (signupSurveySurveyFormElement) {
      signupSurveySurveyFormElement.classList.remove('d-none');
    }
  } else {
    const signupSurveyYoutubeLinkElement = partialDiv.value.querySelector(
      '#signup-survey-youtube-link'
    );
    if (signupSurveyYoutubeLinkElement) {
      signupSurveyYoutubeLinkElement.classList.remove('d-none');
    }
  }
};

onMounted(async () => {
  // Hide back button on success screen (isBackButtonHidden is a Ref designed to be mutated)
  // eslint-disable-next-line vue/no-mutating-props
  props.signupScreen.isBackButtonHidden.value = true;

  partialDiv.value?.addEventListener('code-embed-rendered', initSurveyVisibility, { once: true });
});
onUnmounted(() => {
  // Clean up event listener
  if (partialDiv.value) {
    partialDiv.value.removeEventListener('code-embed-rendered', initSurveyVisibility);
  }
});

const { slug } = useRoute().params;
</script>
